import { GenerateTask, NCNode, PositioningOutputType } from '../../../../src/commonTypes';
import { getUserLikes, like } from '../apiRequests';
import { closeAllPanels, showNonBlockingMessage } from './uiUtils';
import * as tutorialEvents from '../tutorials/tutorialEvents';
import { showSingleImageLightbox } from './lightbox';
import { setActiveSynthesiseDockTab } from './synthesiseDock';
import {
	likedItemsToSavedOutputCards,
	SavedOutputCardData,
	toPositioningOutputSnapshot,
} from './positioningOutputAdapter';
import { findMatchingNodeForLikedItem } from '../shareDbUtils';

type LibraryCategory = 'all' | 'people' | 'ideas' | 'brand' | 'research' | 'media' | 'other';

const ideaBoxButton = document.getElementById('board-menu-hearted');
const ideaBoxContainer = document.getElementById('hearted-container');
const ideaBoxOverrideSelect = document.querySelector<HTMLSelectElement>('#idea-box-override');
const libraryGrid = ideaBoxContainer?.querySelector<HTMLDivElement>('.idea-library-grid');
const searchInput = ideaBoxContainer?.querySelector<HTMLInputElement>('#idea-library-search');
let currentCards: SavedOutputCardData[] = [];
let activeCategory: LibraryCategory = 'all';
let libraryRenderRevision = 0;
let libraryRenderFrame: number | undefined;
const LIBRARY_RENDER_BATCH_SIZE = 24;

const categoryFor = (type: PositioningOutputType): Exclude<LibraryCategory, 'all'> => {
	if (['persona', 'character', 'stakeholder-motivation', 'audience'].includes(type))
		return 'people';
	if (['solution', 'concept'].includes(type)) return 'ideas';
	if (['brand', 'brand-colours', 'story'].includes(type)) return 'brand';
	if (['reference', 'cluster'].includes(type)) return 'research';
	if (['image', 'video'].includes(type)) return 'media';
	return 'other';
};

const legacyContainerClassFor = (sourceType: SavedOutputCardData['sourceType']): string => {
	if (sourceType === 'node') return 'node-container';
	if (sourceType === 'cluster') return 'cluster-container';
	if (sourceType === 'persona' || sourceType === 'character') return 'persona-container';
	if (sourceType === 'motivations') return 'motivations-container';
	if (sourceType === 'haiku' || sourceType === 'ai-haiku') return 'haiku-container';
	if (sourceType === 'search-image' || sourceType.startsWith('image')) return 'image-container';
	return `${sourceType}-container`;
};

/** Prefer structured output id, then inspire uid / url / label matching. */
const boardNodeFor = (card: SavedOutputCardData, nodes: ReadonlyArray<NCNode>): NCNode | undefined => {
	const byOutputId = nodes.find(
		(node) => node.positioningOutput?.sourceOutputId === card.sourceOutputId
	);
	if (byOutputId) return byOutputId;
	return findMatchingNodeForLikedItem(nodes, card.item);
};

const createVisual = (card: SavedOutputCardData): HTMLElement => {
	const visual = document.createElement('div');
	visual.className = 'idea-library-card__visual';
	if (card.payload.kind === 'palette') {
		visual.classList.add('idea-library-card__palette');
		card.payload.palette.colours.slice(0, 6).forEach((colour) => {
			const swatch = document.createElement('span');
			swatch.style.backgroundColor = colour.hex;
			swatch.title = `${colour.name} · ${colour.hex}`;
			visual.append(swatch);
		});
	} else if (card.imageUrl || (card.payload.kind === 'media' && !card.payload.isVideo)) {
		const imageUrl = card.imageUrl || (card.payload.kind === 'media' ? card.payload.url : '');
		const image = document.createElement('img');
		image.src = imageUrl;
		image.alt = '';
		visual.append(image);
		visual.addEventListener('click', (event) => {
			event.stopPropagation();
			showSingleImageLightbox(imageUrl);
		});
	} else {
		visual.classList.add('idea-library-card__monogram');
		visual.textContent = card.title
			.split(/\s+/)
			.slice(0, 2)
			.map((part) => part[0]?.toUpperCase())
			.join('');
	}
	return visual;
};

const addOrUpdateBoardCard = async (card: SavedOutputCardData): Promise<void> => {
	const graph = globalThis.neuroCreate.graph;
	if (!graph) return;
	const node = await graph.addToBoard(card.item, toPositioningOutputSnapshot(card));
	if (node) graph.updateActive(node);
	refreshHeartedList();
};

const deleteSavedOutput = async (card: SavedOutputCardData): Promise<void> => {
	const { boardId } = document.body.dataset;
	if (!boardId) return;
	await like(
		boardId,
		card.item.type,
		card.item.text || null,
		card.item.url || null,
		true,
		card.item.title,
		card.item.topics,
		card.item.nodeUid,
		card.item.positioningContext,
		card.item.clientId
	);
	refreshHeartedList();
};

const createCard = (
	card: SavedOutputCardData,
	existingNode: ReturnType<typeof boardNodeFor>
): HTMLElement => {
	const wrapper = document.createElement('article');
	wrapper.className = `idea-library-card ${legacyContainerClassFor(card.sourceType)}`;
	wrapper.dataset.sourceOutputId = card.sourceOutputId;
	wrapper.dataset.sourceType = card.sourceType;
	wrapper.dataset.category = categoryFor(card.type);
	if (card.ownerColour) wrapper.style.setProperty('--idea-owner-colour', card.ownerColour);

	const content = document.createElement('div');
	content.className = 'idea-library-card__content';
	content.append(createVisual(card));
	const copy = document.createElement('div');
	copy.className = 'idea-library-card__copy';
	const eyebrow = document.createElement('span');
	eyebrow.className = 'idea-library-card__eyebrow';
	eyebrow.textContent = card.eyebrow;
	const title = document.createElement('h3');
	title.textContent = card.title;
	const summary = document.createElement('p');
	summary.textContent = card.summary || card.subtitle || 'Saved output';
	copy.append(eyebrow, title, summary);
	content.append(copy);
	wrapper.append(content);

	const footer = document.createElement('footer');
	const provenance = document.createElement('span');
	provenance.className = 'idea-library-card__provenance';
	const ownerLabel = card.ownerName ? `Added by ${card.ownerName}` : 'Saved';
	provenance.textContent = `${ownerLabel} · v${card.version}${card.likeCount ? ` · ♥ ${card.likeCount}` : ''}`;
	if (card.likedByUsers.length) {
		provenance.title = `Liked by ${card.likedByUsers.map((user) => user.name).join(', ')}`;
	}
	footer.append(provenance);
	const actions = document.createElement('div');
	actions.className = 'idea-library-card__actions';
	if (['moodboard', 'inspire'].includes(globalThis.neuroCreate.graph?.graphMode || '')) {
		const boardButton = document.createElement('button');
		boardButton.type = 'button';
		boardButton.className = 'idea-library-card__board-action';
		if (!existingNode) {
			boardButton.textContent = 'Add to board';
			boardButton.addEventListener('click', () => void addOrUpdateBoardCard(card));
		} else if (
			existingNode.positioningOutput &&
			(existingNode.positioningOutput.version || 0) < card.version
		) {
			boardButton.textContent = 'Update from source';
			boardButton.classList.add('is-update');
			boardButton.addEventListener('click', () => void addOrUpdateBoardCard(card));
		} else {
			boardButton.textContent = 'On board';
			boardButton.disabled = true;
		}
		actions.append(boardButton);
	}
	if (card.ownedByCurrentUser) {
		const remove = document.createElement('button');
		remove.type = 'button';
		remove.className = 'idea-library-card__remove';
		remove.title = 'Remove from Idea Box';
		remove.setAttribute('aria-label', `Remove ${card.title} from Idea Box`);
		remove.textContent = '×';
		remove.addEventListener('click', () => void deleteSavedOutput(card));
		actions.append(remove);
	}
	footer.append(actions);
	wrapper.append(footer);
	return wrapper;
};

const renderLibrary = (onComplete?: () => void): void => {
	if (!libraryGrid) return;
	libraryRenderRevision += 1;
	const renderRevision = libraryRenderRevision;
	if (libraryRenderFrame !== undefined) {
		window.cancelAnimationFrame(libraryRenderFrame);
		libraryRenderFrame = undefined;
	}
	libraryGrid.replaceChildren();
	const search = searchInput?.value.trim().toLocaleLowerCase() || '';
	const visible = currentCards.filter((card) => {
		const matchesCategory = activeCategory === 'all' || categoryFor(card.type) === activeCategory;
		const haystack =
			`${card.eyebrow} ${card.title} ${card.subtitle || ''} ${card.summary}`.toLocaleLowerCase();
		return matchesCategory && (!search || haystack.includes(search));
	});
	if (!visible.length) {
		const empty = document.createElement('p');
		empty.className = 'empty-list';
		empty.textContent = currentCards.length
			? 'No saved ideas match this view.'
			: 'Your Idea Box is empty. Like useful outputs and references to save them here.';
		libraryGrid.append(empty);
		onComplete?.();
		return;
	}

	// Resolve board membership once. Prefer sourceOutputId (O(1)), then
	// fall back to inspire uid / url / label matching for nodes that never
	// got a positioningOutput stamp.
	const boardNodes = globalThis.neuroCreate.graph?.nodes || [];
	const boardNodesByOutputId = new Map(
		boardNodes.flatMap((node) =>
			node.positioningOutput?.sourceOutputId
				? [[node.positioningOutput.sourceOutputId, node] as const]
				: []
		)
	);
	const resolveBoardNode = (card: SavedOutputCardData): NCNode | undefined =>
		boardNodesByOutputId.get(card.sourceOutputId) || boardNodeFor(card, boardNodes);
	let nextCardIndex = 0;
	const appendNextBatch = (): void => {
		if (
			renderRevision !== libraryRenderRevision ||
			ideaBoxContainer?.classList.contains('hidden')
		) {
			libraryRenderFrame = undefined;
			return;
		}
		const fragment = document.createDocumentFragment();
		const batchEnd = Math.min(nextCardIndex + LIBRARY_RENDER_BATCH_SIZE, visible.length);
		for (; nextCardIndex < batchEnd; nextCardIndex += 1) {
			const card = visible[nextCardIndex];
			fragment.append(createCard(card, resolveBoardNode(card)));
		}
		libraryGrid.append(fragment);
		if (nextCardIndex < visible.length) {
			libraryRenderFrame = window.requestAnimationFrame(appendNextBatch);
		} else {
			libraryRenderFrame = undefined;
			onComplete?.();
		}
	};
	appendNextBatch();
};

export const refreshHeartedList = (callback: undefined | (() => void) = undefined) => {
	if (!ideaBoxContainer || ideaBoxContainer.classList.contains('hidden')) return;
	const loading = ideaBoxContainer.querySelector<HTMLElement>('.loading');
	loading?.classList.remove('hidden');
	const { boardId } = document.body.dataset;
	if (!boardId) return;
	const overrideUserId =
		ideaBoxOverrideSelect && ideaBoxOverrideSelect.value !== document.body.dataset.userId
			? ideaBoxOverrideSelect.value
			: undefined;
	getUserLikes(boardId, overrideUserId, window.location.hash.includes('team'))
		.then((likedItems) => {
			currentCards = likedItemsToSavedOutputCards(likedItems);
			renderLibrary(callback);
		})
		.catch(() =>
			showNonBlockingMessage('The Idea Box could not be loaded. Please try again.', [], true)
		)
		.finally(() => loading?.classList.add('hidden'));
};

export const heartedListScrollTo = (
	container: GenerateTask | 'image' | 'link' | 'node' | 'cluster'
): void => {
	if (!ideaBoxContainer) return;
	const map: Partial<Record<typeof container, LibraryCategory>> = {
		persona: 'people',
		motivations: 'people',
		brainstorm: 'ideas',
		node: 'ideas',
		image: 'media',
		link: 'research',
		cluster: 'research',
	};
	activeCategory = map[container] || 'all';
	ideaBoxContainer
		.querySelectorAll<HTMLButtonElement>('[data-library-filter]')
		.forEach((button) =>
			button.classList.toggle('is-active', button.dataset.libraryFilter === activeCategory)
		);
	renderLibrary(() => {
		const sourceTypes: Partial<Record<typeof container, string[]>> = {
			image: ['search-image', 'image-inspire', 'image-synthesise'],
			link: ['search-page', 'article'],
		};
		const matchingTypes = sourceTypes[container] || [container];
		const card = matchingTypes
			.map((type) => ideaBoxContainer.querySelector(`[data-source-type="${type}"]`))
			.find(Boolean);
		(card || ideaBoxContainer.querySelector('.idea-library-card'))?.scrollIntoView({
			block: 'nearest',
		});
	});
};

export const openHeartedList = (callback: undefined | (() => void) = undefined) => {
	if (!ideaBoxButton || !ideaBoxContainer) return;
	closeAllPanels();
	ideaBoxButton.classList.add('buttonActive');
	ideaBoxContainer.classList.remove('hidden');
	setActiveSynthesiseDockTab('idea-box');
	tutorialEvents.openedIdeaBox();
	refreshHeartedList(callback);
};

const closeHeartedList = () => {
	if (!ideaBoxButton || !ideaBoxContainer) return;
	ideaBoxContainer.classList.add('hidden');
	ideaBoxButton.classList.remove('buttonActive');
};

export const isHeartedListOpen = () =>
	Boolean(ideaBoxContainer && !ideaBoxContainer.classList.contains('hidden'));
export const hasHeartedItems = () => currentCards.length > 0;

ideaBoxButton?.addEventListener('click', () =>
	isHeartedListOpen() ? closeHeartedList() : openHeartedList()
);
ideaBoxOverrideSelect?.addEventListener('change', () => refreshHeartedList());
searchInput?.addEventListener('input', () => renderLibrary());
ideaBoxContainer?.querySelectorAll<HTMLButtonElement>('[data-library-filter]').forEach((button) => {
	button.addEventListener('click', () => {
		if (!ideaBoxContainer) return;
		activeCategory = (button.dataset.libraryFilter || 'all') as LibraryCategory;
		ideaBoxContainer
			.querySelectorAll<HTMLButtonElement>('[data-library-filter]')
			.forEach((item) => item.classList.toggle('is-active', item === button));
		renderLibrary();
	});
});
